import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, map, retry, switchMap, take, tap } from 'rxjs/operators';

import {
  CriteriaMatchType,
  DuplicateCriteriaInterface as DupeSettingsApiModel,
  ProspectApiService,
} from '@vendasta/prospect';

export enum DupeSettingsViewModel {
  DUPLICATE_SETTINGS_UNKNOWN,
  DUPLICATE_SETTINGS_USE_PHONE,
  DUPLICATE_SETTINGS_USE_EMAIL,
  DUPLICATE_SETTINGS_EITHER,
  DUPLICATE_SETTINGS_BOTH,
}

@Injectable()
export class LeadsDuplicateDetectionService {
  private readonly isLoading$$ = new BehaviorSubject<boolean>(false);
  public readonly isLoading$ = this.isLoading$$.asObservable();

  private readonly duplicateSettings$$ = new BehaviorSubject<DupeSettingsViewModel>(
    DupeSettingsViewModel.DUPLICATE_SETTINGS_UNKNOWN,
  );
  private readonly newDuplicateSettings$$ = new BehaviorSubject<DupeSettingsViewModel>(null);
  readonly newDuplicateSettings$ = this.newDuplicateSettings$$.asObservable();

  private readonly viewSettingsToApiSettingsMap: Map<DupeSettingsViewModel, DupeSettingsApiModel> = new Map([
    [
      DupeSettingsViewModel.DUPLICATE_SETTINGS_USE_PHONE,
      {
        useEmail: false,
        usePhoneNumber: true,
        criteriaMatchType: CriteriaMatchType.CRITERIA_MATCH_TYPE_ALL,
      },
    ],
    [
      DupeSettingsViewModel.DUPLICATE_SETTINGS_USE_EMAIL,
      {
        useEmail: true,
        usePhoneNumber: false,
        criteriaMatchType: CriteriaMatchType.CRITERIA_MATCH_TYPE_ALL,
      },
    ],
    [
      DupeSettingsViewModel.DUPLICATE_SETTINGS_EITHER,
      {
        useEmail: true,
        usePhoneNumber: true,
        criteriaMatchType: CriteriaMatchType.CRITERIA_MATCH_TYPE_ANY,
      },
    ],
    [
      DupeSettingsViewModel.DUPLICATE_SETTINGS_BOTH,
      {
        useEmail: true,
        usePhoneNumber: true,
        criteriaMatchType: CriteriaMatchType.CRITERIA_MATCH_TYPE_ALL,
      },
    ],
  ]);

  // TODO this.subscriptions.push(loadSubscription); like in SSC, so it can be onDestroyed later
  //      for all the API calls

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly prospectSDK: ProspectApiService,
    private readonly snackbarService: SnackbarService,
    private translateService: TranslateService,
    private readonly router: Router,
  ) {}

  private apiSettingsToViewSettings(settings: DupeSettingsApiModel): DupeSettingsViewModel {
    for (const [viewDef, apiDef] of this.viewSettingsToApiSettingsMap) {
      if (
        settings.useEmail === apiDef.useEmail &&
        settings.usePhoneNumber === apiDef.usePhoneNumber &&
        settings.criteriaMatchType === apiDef.criteriaMatchType
      ) {
        return viewDef;
      }
    }
    return DupeSettingsViewModel.DUPLICATE_SETTINGS_UNKNOWN;
  }

  loadDuplicateConfig(): void {
    this.isLoading$$.next(true);
    this.partnerId$
      .pipe(
        switchMap((pid) => this.prospectSDK.getDuplicateCriteria({ partnerId: pid })),
        retry(1),
        catchError((_) => {
          this.showError('LEAD_INTEGRATIONS.SETTINGS.DUPLICATES.LOAD_ERROR');
          this.duplicateSettings$$.next(DupeSettingsViewModel.DUPLICATE_SETTINGS_UNKNOWN);
          this.newDuplicateSettings$$.next(DupeSettingsViewModel.DUPLICATE_SETTINGS_UNKNOWN);
          return EMPTY;
        }),
        map((response) => {
          // protobuf means zero-ish values get omitted from our object
          return {
            partnerId: response.config.partnerId || '',
            useEmail: response.config.useEmail || false,
            usePhoneNumber: response.config.usePhoneNumber || false,
            criteriaMatchType: response.config.criteriaMatchType || CriteriaMatchType.CRITERIA_MATCH_TYPE_UNKNOWN,
          };
        }),
        take(1),
      )
      .subscribe((response) => {
        const current = this.apiSettingsToViewSettings(response);
        this.duplicateSettings$$.next(current);
        this.newDuplicateSettings$$.next(current);
        this.isLoading$$.next(false);
      });
  }

  updateCriteria(nc: DupeSettingsViewModel): void {
    this.newDuplicateSettings$$.next(nc);
  }

  saveCriteria(): Observable<void> {
    const newSettings = this.newDuplicateSettings$$.getValue();
    const newSettingsApiModel = this.viewSettingsToApiSettingsMap.get(newSettings);
    if (!newSettingsApiModel) {
      this.showError('LEAD_INTEGRATIONS.SETTINGS.DUPLICATES.SAVE_ERROR');
      return;
    }

    return this.partnerId$.pipe(
      tap((_) => {
        this.duplicateSettings$$.next(newSettings);
        this.isLoading$$.next(true);
      }),
      map((pid) => {
        return {
          ...newSettingsApiModel,
          partnerId: pid,
        };
      }),
      switchMap((newApi) => this.prospectSDK.updateDuplicateCriteria({ config: newApi })),
      retry(1),
      catchError((_) => {
        this.showError('LEAD_INTEGRATIONS.SETTINGS.DUPLICATES.SAVE_ERROR');
        this.duplicateSettings$$.next(DupeSettingsViewModel.DUPLICATE_SETTINGS_UNKNOWN);
        this.newDuplicateSettings$$.next(DupeSettingsViewModel.DUPLICATE_SETTINGS_UNKNOWN);
        return EMPTY;
      }),
      map((_) => null),
      tap((_) => this.isLoading$$.next(false)),
      take(1),
    );
  }

  revertCriteria(): void {
    this.router.navigate(['facebook-ad-leads']);
  }

  private showSuccess(textKey: string): void {
    this.snackbarService.successSnack(this.translateService.instant(textKey));
  }

  private showError(textKey: string): void {
    this.snackbarService.errorSnack(this.translateService.instant(textKey));
  }
}
